import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LockClosedIcon, ArrowUpCircleIcon } from '@heroicons/react/24/outline';
import { useSubscription } from '../../hooks/useSubscription';

/**
 * LockedFeatureCard - Shows a teaser card for a locked feature with an upgrade CTA.
 * Designed to look intentional and premium, not broken or disabled.
 *
 * @param {string} feature - The entitlement feature key
 * @param {string} title - Feature name to display
 * @param {string} description - Short description of the feature
 * @param {React.ReactNode} icon - Optional icon element (Heroicon recommended)
 * @param {React.ReactNode} preview - Optional preview content to show behind the lock overlay
 * @param {string} className - Additional CSS classes
 */
const LockedFeatureCard = ({ feature, title, description, icon, preview, className = '' }) => {
  const { getUpgradeForFeature, hasFeature } = useSubscription();
  const navigate = useNavigate();

  // If the user actually has the feature, don't show the locked card
  if (hasFeature(feature)) {
    return null;
  }

  const upgradeInfo = getUpgradeForFeature(feature);
  const recommendedConfig = upgradeInfo?.config;
  const priceLabel = recommendedConfig?.price != null ? `$${recommendedConfig.price}/mo` : 'Custom pricing';

  const handleUpgrade = () => {
    if (upgradeInfo) {
      navigate(`/subscription/checkout?plan=${upgradeInfo.recommendedTier}`);
    } else {
      navigate('/subscription');
    }
  };

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border border-sage-200 bg-gradient-to-b from-white to-sage-50/50 ${className}`}
    >
      {/* Optional preview with blur overlay */}
      {preview && (
        <div className="relative">
          <div className="opacity-15 blur-[2px] pointer-events-none p-4">{preview}</div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white/90" />
        </div>
      )}

      {/* Main content */}
      <div className="relative p-6 text-center">
        {/* Icon area */}
        <div className="relative inline-flex mb-4">
          <div className="w-14 h-14 bg-gradient-to-br from-lavender-100 dark:from-lavender-900/30 to-peach-100 dark:to-peach-900/30 rounded-2xl flex items-center justify-center">
            {icon || <LockClosedIcon className="w-6 h-6 text-lavender-500" />}
          </div>
          {/* Small lock badge */}
          <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-sage-600 rounded-full flex items-center justify-center ring-2 ring-white">
            <LockClosedIcon className="w-3 h-3 text-white" />
          </div>
        </div>

        {/* Title & description */}
        <h3 className="text-base font-semibold text-sage-900 mb-1">{title}</h3>
        <p className="text-sm text-sage-500 mb-5 max-w-xs mx-auto">{description}</p>

        {/* Upgrade CTA */}
        {recommendedConfig && (
          <div className="space-y-3">
            <div className="inline-flex items-center space-x-1.5 text-xs text-sage-500 bg-sage-100 rounded-full px-3 py-1">
              <span>Available on</span>
              <span className={`font-semibold text-${recommendedConfig.color}-600`}>{recommendedConfig.name}</span>
              <span>({priceLabel})</span>
            </div>

            <div>
              <button
                onClick={handleUpgrade}
                className="inline-flex items-center space-x-2 bg-gradient-to-r from-lavender-500 to-lavender-600 text-white px-5 py-2.5 rounded-xl text-sm font-medium hover:from-lavender-600 hover:to-lavender-700 transition-all shadow-xs"
              >
                <ArrowUpCircleIcon className="w-4 h-4" />
                <span>Upgrade to Unlock</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LockedFeatureCard;
